import { Loader2Icon } from 'lucide-react';

import { cn } from '@/lib/utils';

interface SpinnerProps extends React.ComponentProps<'span'> {
  label?: React.ReactNode;
  iconClassName?: string;
  /**
   * Layout of the wrapper:
   * - `'inline'` (default): sits inline with surrounding text/buttons.
   * - `'center'`: centered row on its own line, with vertical padding — for section loading states.
   * - `'fill'`: fills and centers within a sized parent — for fixed-size boxes (e.g. an avatar slot).
   */
  variant?: 'inline' | 'center' | 'fill';
}

const variantClass = {
  inline: 'inline-flex',
  center: 'flex w-full justify-center py-4',
  fill: 'flex h-full w-full justify-center',
} as const;

function Spinner({
  label,
  iconClassName,
  variant = 'inline',
  className,
  'aria-label': ariaLabel = 'Loading',
  ...props
}: SpinnerProps) {
  return (
    <span
      data-slot="spinner"
      role="status"
      aria-label={ariaLabel}
      className={cn(
        'text-muted-foreground items-center gap-2 text-sm',
        variantClass[variant],
        className,
      )}
      {...props}
    >
      <Loader2Icon
        data-slot="spinner-icon"
        aria-hidden="true"
        className={cn('size-4 animate-spin', iconClassName)}
      />
      {label != null && <span>{label}</span>}
    </span>
  );
}

export { Spinner };
